import { captureException } from "@mixitone/telemetry";
import { applyUpdate, checkUpdate, configure, notifyReady } from "tauri-plugin-hotswap-api";
import { resolveUpdateChannel, shouldCheckForUpdates } from "updater/autoUpdate";
import tauri from "lib/tauri";

export type HotswapCheckResult =
  | { kind: "unavailable" }
  | { kind: "applied" }
  | { kind: "error"; error: unknown };

/** Confirm the running bundle on every successful startup (rollback safety). */
export async function confirmHotswapReady(): Promise<void> {
  if (!tauri.enabled || !shouldCheckForUpdates()) return;
  try {
    await notifyReady();
  } catch (err) {
    captureException(err, { extra: { context: "hotswap.notifyReady" } });
  }
}

/**
 * Check for a signed frontend OTA bundle and apply it (reloads the webview).
 * Returns whether an update was applied so callers can skip binary update checks.
 */
export async function tryHotswapFrontendUpdate(): Promise<HotswapCheckResult> {
  if (!tauri.enabled || !shouldCheckForUpdates()) {
    return { kind: "unavailable" };
  }

  try {
    const channel = await resolveUpdateChannel();
    await configure({ channel });

    const result = await checkUpdate();
    if (!result.available) {
      return { kind: "unavailable" };
    }

    await applyUpdate();
    window.location.reload();
    return { kind: "applied" };
  } catch (err) {
    console.error("[hotswap] update failed", err);
    captureException(err, { extra: { context: "hotswap.tryHotswapFrontendUpdate" } });
    return { kind: "error", error: err };
  }
}
